import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdNotifications } from 'react-icons/md';
import Box from '@mui/material/Box';
import useMarkAllNotificationsAsRead from '../../hooks/mutations/useMarkAllNotificationsAsRead';
import useMarkNotificationAsRead from '../../hooks/mutations/useMarkNotificationAsRead';
import useGetNotifications from '../../hooks/useGetNotifications';
import useGetUnreadNotificationCount from '../../hooks/useGetUnreadNotificationCount';

const TYPE_BADGE_COLORS = {
    announcement_published: { bg: '#dbeafe', color: '#1d4ed8' },
    leave_approved: { bg: '#d1fae5', color: '#047857' },
    leave_rejected: { bg: '#ffe4e6', color: '#be123c' },
    attendance_alert: { bg: '#fef3c7', color: '#b45309' },
    payroll_processed: { bg: '#d1fae5', color: '#047857' },
};
const DEFAULT_BADGE_COLOR = { bg: '#dbeafe', color: '#1d4ed8' };

const Notifications = () => {
    const [showNotifications, setShowNotifications] = useState(false);
    const dropdownRef = useRef(null);
    const listRef = useRef(null);
    const sentinelRef = useRef(null);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
    } = useGetNotifications({ enabled: showNotifications });

    const { mutateAsync: markAsRead } = useMarkNotificationAsRead();
    const { mutateAsync: markAllAsRead, isPending: isMarkingAllAsRead } = useMarkAllNotificationsAsRead();
    const { data: unreadCountData } = useGetUnreadNotificationCount();

    const notifications = data?.pages?.flatMap((page) => page?.response_data?.data ?? []) ?? [];
    const unreadCount = unreadCountData?.response_data?.unread_count ?? 0;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!showNotifications) return;
        const sentinel = sentinelRef.current;
        const root = listRef.current;
        if (!sentinel || !root) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { root, threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [showNotifications, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleMarkAsRead = useCallback(
        (id) => {
            markAsRead(id);
        },
        [markAsRead]
    );

    const handleMarkAllAsRead = useCallback(() => {
        markAllAsRead();
    }, [markAllAsRead]);

    const getTypeBadge = (type) => TYPE_BADGE_COLORS[type] || DEFAULT_BADGE_COLOR;

    const getTypeLabel = (type) => {
        if (!type) return 'Info';
        return type
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <Box sx={{ position: 'relative' }} ref={dropdownRef}>
            {/* Notification Button */}
            <Box
                component="button"
                onClick={() => setShowNotifications(!showNotifications)}
                aria-label="Notifications"
                title={unreadCount > 0 ? `${unreadCount} unread notifications` : 'No new notifications'}
                sx={{
                    position: 'relative',
                    p: 1,
                    color: '#64748b',
                    borderRadius: '9999px',
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    transition: 'background-color 150ms',
                    '&:hover': { bgcolor: '#f1f5f9' },
                }}
            >
                <MdNotifications size={20} />
                {unreadCount > 0 && (
                    <Box
                        component="span"
                        sx={{
                            position: 'absolute',
                            top: '6px',
                            right: '8px',
                            width: '8px',
                            height: '8px',
                            bgcolor: '#f43f5e',
                            borderRadius: '9999px',
                            border: '2px solid #ffffff',
                            animation: 'notif-pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
                            '@keyframes notif-pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }}
                    />
                )}
            </Box>

            {/* Notifications Dropdown */}
            {showNotifications && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: '0.75rem',
                        right: '0.75rem',
                        top: '4rem',
                        width: 'auto',
                        bgcolor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.75rem',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        zIndex: 1300,
                        overflow: 'hidden',
                        maxHeight: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                        animation: 'notif-slideDown 200ms ease-out',
                        '@keyframes notif-slideDown': {
                            from: { opacity: 0, transform: 'translateY(-10px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        '@media (min-width:640px)': {
                            position: 'absolute',
                            left: 'auto',
                            top: 'auto',
                            right: 0,
                            mt: 1,
                            width: '480px',
                            maxHeight: '500px',
                        },
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box component="h3" sx={{ m: 0, fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>
                            Notifications {unreadCount > 0 && `(${unreadCount})`}
                        </Box>
                        {unreadCount > 0 && (
                            <Box
                                component="button"
                                onClick={handleMarkAllAsRead}
                                disabled={isMarkingAllAsRead}
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: '#2563eb',
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: 'pointer',
                                    transition: 'color 150ms',
                                    '&:hover': { color: '#1d4ed8' },
                                    '&:disabled': { opacity: 0.5, cursor: 'default' },
                                }}
                            >
                                Mark all as read
                            </Box>
                        )}
                    </Box>

                    {/* Notifications List */}
                    <Box ref={listRef} sx={{ overflowY: 'auto', flex: 1 }}>
                        {isLoading ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Box component="p" sx={{ m: 0, fontSize: '0.875rem', color: '#64748b' }}>
                                    Loading...
                                </Box>
                            </Box>
                        ) : notifications.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <MdNotifications size={32} style={{ margin: '0 auto 0.5rem', color: '#cbd5e1', display: 'block' }} />
                                <Box component="p" sx={{ m: 0, fontSize: '0.875rem', color: '#64748b' }}>
                                    No notifications yet
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ '& > *:not(:first-of-type)': { borderTop: '1px solid #f1f5f9' } }}>
                                {notifications.map((notif) => {
                                    const isRead = Boolean(notif.read_at);
                                    const notifType = notif.data?.type;
                                    const badge = getTypeBadge(notifType);
                                    return (
                                        <Box
                                            key={notif.id}
                                            sx={{
                                                p: 2,
                                                bgcolor: !isRead ? '#f8fafc' : 'transparent',
                                                transition: 'background-color 150ms',
                                                '&:hover': { bgcolor: '#f8fafc' },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                {/* Content */}
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'inline-block',
                                                            mb: 0.5,
                                                            px: 1,
                                                            py: '2px',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            bgcolor: badge.bg,
                                                            color: badge.color,
                                                        }}
                                                    >
                                                        {getTypeLabel(notifType)}
                                                    </Box>
                                                    <Box
                                                        component="p"
                                                        sx={{
                                                            m: 0,
                                                            fontSize: '0.875rem',
                                                            fontWeight: 500,
                                                            color: '#0f172a',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {notif.data?.title}
                                                    </Box>
                                                    <Box
                                                        component="p"
                                                        sx={{
                                                            m: 0,
                                                            mt: '2px',
                                                            fontSize: '0.75rem',
                                                            color: '#475569',
                                                            wordBreak: 'break-word',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {notif.data?.message || notif.data?.description}
                                                    </Box>
                                                    <Box component="p" sx={{ m: 0, mt: 0.5, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                        {notif.created_at ? format(new Date(notif.created_at.replace(' ', 'T')), 'MMM d, h:mm a') : ''}
                                                    </Box>
                                                </Box>
                                                {/* Unread Indicator */}
                                                {!isRead && (
                                                    <Box
                                                        component="button"
                                                        onClick={() => handleMarkAsRead(notif.id)}
                                                        title="Mark as read"
                                                        sx={{
                                                            p: '4px',
                                                            flexShrink: 0,
                                                            color: '#2563eb',
                                                            border: 'none',
                                                            bgcolor: 'transparent',
                                                            cursor: 'pointer',
                                                            borderRadius: '0.25rem',
                                                            transition: 'background-color 150ms',
                                                            display: 'inline-flex',
                                                            '&:hover': { bgcolor: '#eff6ff' },
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                width: '8px',
                                                                height: '8px',
                                                                borderRadius: '9999px',
                                                                bgcolor: '#2563eb',
                                                                display: 'block',
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                                {/* Infinite scroll sentinel */}
                                <Box ref={sentinelRef} sx={{ height: '0.25rem' }} />
                                {isFetchingNextPage && (
                                    <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Box component="p" sx={{ m: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                                            Loading more...
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default Notifications;
